<script lang="ts" setup>
import { computed, onMounted, reactive, ref, watchEffect } from 'vue';
import { isValidPhoneNumber } from 'libphonenumber-js';
import {
  FieldInput,
  FieldCheckbox,
  FieldPhoneNumber,
  BaseButton,
  BaseLoader,
  BaseIcon,
} from '@magma-app/magma-lapilli';
import NoAccessView from '@/views/NoAccessView.vue';
import TurnstileWidget from '@/components/security/TurnstileWidget.vue';
import { useTurnstileAlert } from '@/composables/useTurnstileAlert';
import { useReferralInvite } from '@/api/referrals/use-referral-invite';
import { useRoute } from 'vue-router';
import { useCreateHelpee } from '@/api/referrals/use-create-helpee';
import { useResendCode } from '@/api/referrals/use-resend-code';
import { useVerifyCode } from '@/api/referrals/use-verify-code';
import { useResendTimer } from '@/composables/useResendTimer';
import { matchesZodEmail } from '@/utils/emailValidation';
import { DEFAULT_TERMS_URL } from '@/utils/legal';

const route = useRoute();
const { data: referred, isLoading, isError, isFetching, refetch } = useReferralInvite({
  referralCampaignUuid:
    (route.query.referralCampaignUuid as string) || undefined,
  referredUuid: (route.query.referredUuid as string) || undefined,
  ambassadorRefId: (route.query.ambassadorRefId as string) || undefined,
});
const { mutateAsync, status, data: helpeeData } = useCreateHelpee();
const { mutateAsync: resendCode } = useResendCode();
const { mutateAsync: verifyCode, status: statusVerify } = useVerifyCode();
const { showTurnstileErrorAlert } = useTurnstileAlert();

// Use shared timer that persists across component remounts
const {
  timerBeforeResend,
  canResend,
  isResending,
  handleResend,
  startTimer,
  resetTimer,
} = useResendTimer();

const currentStep = ref<'info' | 'code' | 'finish'>('info');
const lastPhoneVerificationNumber = ref<string | null>(null);
const turnstileSiteKey = (import.meta.env.VITE_TURNSTILE_SITE_KEY || '').trim();
const isTurnstileEnabled = computed(() => Boolean(turnstileSiteKey));
const turnstileRef = ref<InstanceType<typeof TurnstileWidget> | null>(null);
const isTurnstileSubmitting = ref(false);

const data = ref({
  firstname: referred.value?.referred.firstname || '',
  lastname: referred.value?.referred.lastname || '',
  email: referred.value?.referred.email || '',
  phone: referred.value?.referred.phoneNumber || '',
  accept: false,
  code: '',
});
const error = ref<string | null>(null);
const timerBeforeRedirect = reactive({ value: 15 });

const validMail = computed(() => {
  return matchesZodEmail(data.value.email);
});

const validPhone = computed(() => {
  const phoneNumber = data.value.phone?.trim();
  return Boolean(phoneNumber) && isValidPhoneNumber(phoneNumber);
});

const isCreateHelpeeFormValid = computed(() => {
  return (
    data.value.firstname.trim() !== '' &&
    data.value.lastname.trim() !== '' &&
    data.value.accept &&
    validMail.value &&
    validPhone.value
  );
});

const handleCreateHelpee = async () => {
  if (
    isError.value || !referred.value ||
    !isCreateHelpeeFormValid.value ||
    status.value === 'pending' ||
    isTurnstileSubmitting.value
  ) {
    return;
  }

  const shouldReuseCurrentPhoneVerification =
    lastPhoneVerificationNumber.value === data.value.phone && !canResend.value;

  if (shouldReuseCurrentPhoneVerification) {
    currentStep.value = 'code';
    return;
  }

  let turnstileToken: string | undefined;
  try {
    if (isTurnstileEnabled.value) {
      try {
        isTurnstileSubmitting.value = true;
        turnstileToken = await turnstileRef.value?.executeVerification();
        if (!turnstileToken) {
          throw new Error('TURNSTILE-TOKEN-MISSING');
        }
      } catch {
        showTurnstileErrorAlert();
        return;
      } finally {
        isTurnstileSubmitting.value = false;
      }
    }

    await mutateAsync({
      firstname: data.value.firstname,
      lastname: data.value.lastname,
      email: data.value.email,
      phoneNumber: data.value.phone,
      referredUuid: route.query.referredUuid as string | undefined,
      referralCampaignUuid: route.query.referralCampaignUuid as
        | string
        | undefined,
      ambassadorRefId: route.query.ambassadorRefId as string | undefined,
      turnstileToken,
    });

    // Start the resend timer when code is sent
    lastPhoneVerificationNumber.value = data.value.phone;
    startTimer();
    currentStep.value = 'code';
  } catch (e) {
    // Interceptor already handles user-facing errors via toast.
  } finally {
    turnstileRef.value?.reset();
  }
};

const handleResendCodeAction = async () => {
  const referredUuid =
    helpeeData?.value?.uuid ?? (route.query.referredUuid as string | undefined);
  if (!referredUuid) throw new Error('MISSING-REFERRED-UUID');

  await handleResend(async () => {
    await resendCode({
      referredUuid,
      phoneNumber: data.value.phone,
    });
  });
};

const handleVerifyCode = async () => {
  const referredUuid =
    helpeeData?.value?.uuid ?? (route.query.referredUuid as string | undefined);
  if (!referredUuid) throw new Error('MISSING-REFERRED-UUID');

  await verifyCode({
    referredUuid,
    code: data.value.code,
  }).then((d) => {
    if (d.message === 'ok') {
      // Reset timer when verification is successful
      lastPhoneVerificationNumber.value = null;
      resetTimer();
      currentStep.value = 'finish';
    } else {
      error.value = 'Code is invalid';
    }
  });
};

const handleRedirect = () => {
  if (referred.value?.redirectUrl) {
    window.location.href = referred.value.redirectUrl;
  }
};

watchEffect(() => {
  if (currentStep.value === 'finish') {
    if (timerBeforeRedirect.value > 0) {
      setTimeout(() => {
        timerBeforeRedirect.value -= 1;
      }, 1000);
    } else {
      handleRedirect();
    }
  }
});

watchEffect(() => {
  if (referred.value) {
    data.value.firstname = referred.value.referred.firstname;
    data.value.lastname = referred.value.referred.lastname;
    data.value.email = referred.value.referred.email;
    data.value.phone = referred.value.referred.phoneNumber || '';
  }
});

watchEffect(() => {
  if (referred.value && referred.value.status !== 'waitingReferredResponse') {
    currentStep.value = 'finish';
  }
});

onMounted(() => {
  const input = document.querySelector('input');
  if (input) {
    input.focus();
  }
});

</script>

<template>
  <div
    class="flex flex-col bg-background-subtle lg:justify-center lg:items-center"
  >
    <BaseLoader v-if="isLoading" class="min-h-screen" />
    <NoAccessView
      v-else-if="isError || !referred"
      class="w-full"
      variant="invitation"
      :retrying="isFetching"
      @retry="refetch()"
    />
    <template v-else>
      <div
        class="flex-1 flex flex-col md:min-h-0 md:flex-none md:w-[500px] lg:!w-[500px] xl:!w-[600px] md:mx-auto bg-white md:border md:border-border-subtle md:h-fit md:rounded-2xl md:mt-10 xl:mt-20 md:shadow-lg relative overflow-hidden"
      >
        <div
          class="flex w-full border-b border-border-subtle h-16 items-center gap-3 px-6"
        >
          <img
            :src="referred?.organization.logoUrl"
            class="w-8 h-8 rounded-full"
          />
          <p class="text-mgm-txt-sm text-foreground-emphasis font-medium">
            {{ referred?.organization.name }}
          </p>
        </div>
        <div
          v-if="currentStep === 'info'"
          class="p-6 flex flex-col h-[calc(100dvh_-_64px)] md:h-fit"
        >
          <div class="flex-1">
            <p
              class="text-mgm-txt-lg text-foreground-emphasis font-medium mb-3"
            >
              {{
                $t('referral.invite.title', {
                  firstname: referred?.ambassador.firstname,
                  organizationName: referred?.organization.name
                })
              }}
            </p>

            <FieldInput
              v-model="data.firstname"
              :label="$t('referral.invite.firstname')"
              placeholder="Camille"
              class="mb-3"
              required
            />
            <FieldInput
              v-model="data.lastname"
              :label="$t('referral.invite.lastname')"
              placeholder="Durand"
              class="mb-3"
              required
            />
            <FieldInput
              v-model="data.email"
              type="email"
              :label="$t('referral.invite.email')"
              placeholder="camille@example.com"
              class="mb-3"
              required
            />
          <FieldPhoneNumber
            v-model="data.phone"
            label="Phone number"
            description="We'll text you a code to verify your number."
            class="mb-3"
            required
          />
            <FieldCheckbox id="accept" v-model="data.accept" label>
              <span class="text-mgm-txt-sm"
                >{{ $t('generic.agree') }}
                <a
                  href="https://fr.magma.app/legals/supported-terms-of-use"
                  target="_blank"
                  rel="noreferrer noopener"
                  class="underline text-foreground-brand-default"
                  >{{ $t('generic.termsAndPrivacy.part2') }}</a
                >
                {{ $t('generic.termsAndPrivacy.part3') }}
                <a
                  :href="referred?.organization.termsUrl || DEFAULT_TERMS_URL"
                  target="_blank"
                  rel="noreferrer noopener"
                  class="underline text-foreground-brand-default"
                  >{{ $t('generic.termsAndPrivacy.part4') }}</a
                ></span
              >
            </FieldCheckbox>
            <TurnstileWidget
              v-if="isTurnstileEnabled"
              ref="turnstileRef"
              class="mt-4"
              :site-key="turnstileSiteKey"
              action="signup-referred"
              execution="execute"
              appearance="interaction-only"
            />
          </div>
          <p class="text-mgm-txt-sm text-foreground-default mt-8">
            {{
              $t('referral.invite.disclaimer', {
                organizationName: referred?.organization.name
              })
            }}
          </p>
          <BaseButton
            size="lg"
            class="w-full mt-4"
            :disabled="
              !isCreateHelpeeFormValid ||
              isTurnstileSubmitting ||
              status === 'pending'
            "
            @click="handleCreateHelpee"
            ><span class="inline-flex items-center justify-center gap-2">
              <span
                v-if="isTurnstileSubmitting || status === 'pending'"
                class="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"
                aria-hidden="true"
              />
              <span>{{ $t('referral.invite.cta') }}</span>
            </span></BaseButton
          >
        </div>
        <div
          v-if="currentStep === 'code'"
          class="p-6 flex flex-col h-[calc(100dvh_-_64px)] md:h-fit"
        >
          <div class="flex-1">
            <p
              class="text-mgm-txt-lg text-foreground-emphasis font-medium mb-3"
            >
              {{ $t('helperOnboarding.verification.title') }}
            </p>

            <h3 class="text-mgm-txt-sm text-foreground-default mb-6">
              {{ $t('helperOnboarding.verification.subtitle.text') }}(<button
                class="underline"
                @click="currentStep = 'info'"
              >
                {{ $t('helperOnboarding.verification.subtitle.link') }}</button
              >)
            </h3>
            <FieldInput
              id="code"
              v-model="data.code"
              type="number"
              autocomplete="one-time-code"
              max-length="6"
              min-length="6"
              data-cy="code"
              class="mt-4"
              :locale="$i18n.locale"
              :label="$t('helpeeCampaignSignUpView.codeLabel')"
              required
            />

            <p
              v-if="error"
              class="text-mgm-txt-xs text-border-danger-emphasis mt-3"
            >
              {{ error }}
            </p>

            <div class="flex items-center flex-col mt-4">
              <p class="text-foreground-default text-mgm-txt-sm mb-1">
                {{ $t('helpeeCampaignSignUpView.noReceive') }}
              </p>
              <p
                v-if="!canResend"
                class="text-foreground-default text-mgm-txt-sm font-medium mb-10"
              >
                <template v-if="isResending">
                  {{ $t('generic.loading') }}
                </template>
                <template v-else>
                  {{ $t('helpeeCampaignSignUpView.code.resend') }}
                  {{ timerBeforeResend }}
                  {{ $t('helpeeCampaignSignUpView.code.seconds') }}
                </template>
              </p>
              <button
                v-else
                class="text-foreground-brand-default text-mgm-txt-sm flex items-center mb-10"
                @click="handleResendCodeAction"
              >
                <BaseIcon
                  icon="refresh-cw"
                  class="mr-2 -scale-x-100"
                  color="rgb(68 76 231)"
                />
                <span class="underline">{{
                  $t('helpeeCampaignSignUpView.resendCode')
                }}</span>
              </button>
            </div>
          </div>
          <BaseButton
            size="lg"
            class="w-full mt-4"
            :disabled="data.code.length !== 6 || statusVerify === 'pending'"
            @click="handleVerifyCode"
            >{{ $t('generic.confirm') }}</BaseButton
          >
        </div>
        <div
          v-if="currentStep === 'finish'"
          class="p-6 flex flex-col h-[calc(100dvh_-_64px)] md:h-fit md:py-20"
        >
          <div class="flex-1 flex justify-center items-center flex-col">
            <div
              class="w-12 h-12 bg-[#D1FADF] rounded-full border-8 border-[#ECFDF3] flex items-center justify-center mb-5"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M20 6L9 17L4 12"
                  stroke="#03874C"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </div>
            <p
              class="text-mgm-dspl-xs text-foreground-emphasis font-medium mb-1"
            >
              {{ $t('referral.invite.thank.title') }}
            </p>
            <p
              class="w-2/3 mx-auto text-center text-mgm-txt-sm text-foreground-default mb-5"
            >
              {{
                $t('referral.invite.thank.text', {
                  organizationName: referred?.organization.name,
                })
              }}
            </p>
            <p
              v-if="referred?.redirectUrl && referred?.redirectUrl !== ''"
              class="text-mgm-txt-sm text-foreground-default"
            >
              {{ $t('referral.invite.thank.redirect') }}
              {{ timerBeforeRedirect.value }}
            </p>
          </div>
        </div>
      </div>
      <a
        class="mx-auto mt-8 text-center text-mgm-txt-xs font-normal text-foreground-default hover:text-foreground-default focus:text-foreground-default hidden lg:flex items-center"
        href="https://www.magma.app/"
        target="_blank"
      >
        {{ $t('generic.powered') }}
        <img class="ml-2" src="/img/logo_magma_full_bw.svg" />
      </a>
    </template>
  </div>
</template>
